/**
 * HTML → PDF for customer receive payments (Puppeteer on server).
 * Classic printable receipt layout (customer-facing), not a dashboard UI.
 */

export function escapeHtmlForPdf(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function formatMoneyPdf(value) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return '0.00';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Format date as US MM/DD/YYYY (accepts YYYY-MM-DD, ISO, or Date). */
export function formatDateUsPdf(value) {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  const ymd = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[2]}/${ymd[3]}/${ymd[1]}`;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function recordType(record) {
  return String(record?.type || record?.entryType || 'item').toLowerCase();
}

/** Prefer explicit origin; fall back to invoice-item link (same as allocation UI). */
function adjustmentOrigin(record) {
  if (record?.bonusDiscountOrigin === 'receipt') return 'receipt';
  if (record?.bonusDiscountOrigin === 'invoice') return 'invoice';
  if (record?.customerInvoiceItemId != null && record.customerInvoiceItemId !== '') return 'invoice';
  return 'receipt';
}

function lineAmount(record) {
  const t = recordType(record);
  if (t === 'discount') {
    const d = parseFloat(record.discountAmount ?? record.totalAmount ?? record.entryTotalAmount);
    return Number.isFinite(d) ? Math.abs(d) : 0;
  }
  const a = parseFloat(record.totalAmount ?? record.entryTotalAmount ?? record.paymentAmount);
  return Number.isFinite(a) ? Math.abs(a) : 0;
}

function moneyCell(amount, { signed = false, negative = false } = {}) {
  const abs = formatMoneyPdf(Math.abs(amount));
  if (negative || (signed && amount < 0)) return `−$${abs}`;
  if (signed && amount > 0) return `$${abs}`;
  return `$${abs}`;
}

function typeLabel(record) {
  const t = recordType(record);
  if (t === 'tax') return 'Sales tax';
  if (t === 'bonus') {
    return adjustmentOrigin(record) === 'receipt' ? 'Bonus (receipt)' : 'Bonus (invoice)';
  }
  if (t === 'discount') {
    return adjustmentOrigin(record) === 'receipt' ? 'Discount (receipt)' : 'Discount (invoice)';
  }
  if (t === 'item' && String(record.itemType || '').toLowerCase() === 'consultant') return 'Consultant';
  if (t === 'item') return 'Service';
  return t || 'Item';
}

function lineDescription(record) {
  const t = recordType(record);
  if (record.lineDescription || record.lineDetail) {
    const parts = [record.lineDescription, record.lineDetail].filter(Boolean);
    return escapeHtmlForPdf(parts.join(' — '));
  }
  if (t === 'tax') return escapeHtmlForPdf(record.taxInput || record.description || 'Sales tax');
  if (t === 'bonus') return escapeHtmlForPdf(record.description || 'Bonus');
  if (t === 'discount') return escapeHtmlForPdf(record.discountInput || record.description || 'Discount');
  const name = record.consultantName || 'Service';
  const hours = record.hours;
  const rate = record.hourlyRate;
  if (hours != null && rate != null && hours !== '' && rate !== '') {
    return escapeHtmlForPdf(`${name} — ${hours} hrs × $${formatMoneyPdf(rate)}`);
  }
  return escapeHtmlForPdf(name);
}

function sortRecords(records) {
  const typeOrder = { item: 1, tax: 2, bonus: 3, discount: 4 };
  return [...records].sort((a, b) => {
    const aType = recordType(a);
    const bType = recordType(b);
    const typeDiff = (typeOrder[aType] || 99) - (typeOrder[bType] || 99);
    if (typeDiff !== 0) return typeDiff;
    if ((aType === 'bonus' || aType === 'discount') && aType === bType) {
      const aReceipt = adjustmentOrigin(a) === 'receipt' ? 1 : 0;
      const bReceipt = adjustmentOrigin(b) === 'receipt' ? 1 : 0;
      if (aReceipt !== bReceipt) return aReceipt - bReceipt;
    }
    return 0;
  });
}

function flattenInvoiceRecords(inv) {
  const cgs = Array.isArray(inv.consultantGroups) ? inv.consultantGroups : [];
  const out = [];
  cgs.forEach((cg) => {
    (cg.records || []).forEach((r) => out.push(r));
  });
  return sortRecords(out);
}

function recordsForInvoice(inv, allocationRows) {
  if (Array.isArray(allocationRows) && allocationRows.length) {
    const invId = inv.invoiceId;
    const invNo = String(inv.invoiceNo || '');
    const filtered =
      invId != null && invId !== ''
        ? allocationRows.filter((r) => Number(r.invoiceId) === Number(invId))
        : allocationRows.filter((r) => String(r.invoiceNo || '') === invNo);
    if (filtered.length) {
      return sortRecords(
        filtered.map((r) => ({
          ...r,
          type: r.entryType || r.type,
          totalAmount: r.entryTotalAmount ?? r.totalAmount ?? r.discountAmount ?? r.paymentAmount,
        }))
      );
    }
  }
  return flattenInvoiceRecords(inv);
}

function summarizeRecords(records) {
  const s = {
    services: 0,
    tax: 0,
    bonusInvoice: 0,
    bonusReceipt: 0,
    discountInvoice: 0,
    discountReceipt: 0,
  };
  records.forEach((r) => {
    const amt = lineAmount(r);
    const t = recordType(r);
    const origin = adjustmentOrigin(r);
    if (t === 'discount') {
      if (origin === 'receipt') s.discountReceipt += amt;
      else s.discountInvoice += amt;
    } else if (t === 'bonus') {
      if (origin === 'receipt') s.bonusReceipt += amt;
      else s.bonusInvoice += amt;
    } else if (t === 'tax') {
      s.tax += amt;
    } else {
      s.services += amt;
    }
  });
  Object.keys(s).forEach((k) => {
    s[k] = round2(s[k]);
  });
  s.subtotal = round2(s.services + s.tax + s.bonusInvoice + s.bonusReceipt);
  s.received = Math.max(
    0,
    round2(s.subtotal - s.discountInvoice - s.discountReceipt)
  );
  return s;
}

function formatInvoiceStatusLabel(status) {
  const s = String(status || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_');
  const map = {
    paid: 'Received',
    partially_paid: 'Partially received',
    overdue: 'Overdue',
    sent: 'Sent',
    draft: 'Draft',
    cancelled: 'Cancelled',
    received: 'Received',
  };
  return escapeHtmlForPdf(map[s] || (status ? String(status) : ''));
}

function buildLineRows(records) {
  if (!records.length) {
    return '<tr><td colspan="2" class="muted">No lines on this receipt.</td></tr>';
  }
  return records
    .map((r) => {
      const t = recordType(r);
      const amt = lineAmount(r);
      const amountText =
        t === 'discount'
          ? moneyCell(amt, { negative: true })
          : moneyCell(amt);
      return `
      <tr>
        <td class="col-desc">
          <div class="desc-title">${escapeHtmlForPdf(typeLabel(r))}</div>
          <div class="desc-detail">${lineDescription(r)}</div>
        </td>
        <td class="col-amt">${amountText}</td>
      </tr>`;
    })
    .join('');
}

function buildTotalsBlock(summary) {
  const rows = [];
  rows.push(`<tr><td>Services / lines</td><td>${moneyCell(summary.services)}</td></tr>`);
  if (summary.tax > 0) {
    rows.push(`<tr><td>Sales tax</td><td>${moneyCell(summary.tax)}</td></tr>`);
  }
  if (summary.bonusInvoice > 0) {
    rows.push(`<tr><td>Bonus (invoice)</td><td>${moneyCell(summary.bonusInvoice)}</td></tr>`);
  }
  if (summary.bonusReceipt > 0) {
    rows.push(`<tr><td>Bonus (receipt)</td><td>${moneyCell(summary.bonusReceipt)}</td></tr>`);
  }
  rows.push(`<tr class="tot-sub"><td>Subtotal</td><td>${moneyCell(summary.subtotal)}</td></tr>`);
  if (summary.discountInvoice > 0) {
    rows.push(`<tr><td>Discount (invoice)</td><td>${moneyCell(summary.discountInvoice, { negative: true })}</td></tr>`);
  }
  if (summary.discountReceipt > 0) {
    rows.push(`<tr><td>Discount (receipt)</td><td>${moneyCell(summary.discountReceipt, { negative: true })}</td></tr>`);
  }
  rows.push(
    `<tr class="tot-final"><td>Amount received</td><td>${moneyCell(summary.received)}</td></tr>`
  );
  return `<table class="totals-table">${rows.join('')}</table>`;
}

/**
 * @param {{ companyName: string, group: object }} opts
 */
export function buildReceivePaymentReceiptPdfHtml({ companyName, group }) {
  const co = escapeHtmlForPdf(companyName || 'Company');
  const ref = escapeHtmlForPdf(group.reference || group.paymentNo || '');
  const payDate = escapeHtmlForPdf(formatDateUsPdf(group.paymentDate || ''));
  const method = escapeHtmlForPdf(group.paymentMethod || '');
  const customer = escapeHtmlForPdf(group.customerName || '');
  const total = formatMoneyPdf(group.totalReceived);
  const generated = escapeHtmlForPdf(
    new Date().toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  );
  const allocationRows = Array.isArray(group.allocationRows) ? group.allocationRows : null;
  const invoices = Array.isArray(group.invoices) ? group.invoices : [];

  const receiptDetailsHtml = `
    <div class="panel panel--receipt">
      <div class="panel-title">Receipt details</div>
      <table class="meta">
        <tr><td class="k">Reference</td><td class="v">${ref}</td></tr>
        <tr><td class="k">Payment date</td><td class="v">${payDate}</td></tr>
        <tr><td class="k">Customer</td><td class="v">${customer}</td></tr>
        <tr><td class="k">Payment method</td><td class="v">${method}</td></tr>
      </table>
    </div>`;

  let appliedInvoiceHtml = '';
  if (!invoices.length) {
    appliedInvoiceHtml = `
    <div class="panel panel--invoice">
      <div class="panel-title">Applied to invoice</div>
      <p class="muted">No invoices on this receipt.</p>
    </div>`;
  } else {
    const invItems = invoices
      .map((inv, idx) => {
        const invNo = escapeHtmlForPdf(inv.invoiceNo || '');
        const invDate = escapeHtmlForPdf(formatDateUsPdf(inv.invoiceDate || ''));
        const statusLabel = formatInvoiceStatusLabel(inv.invoiceStatus);
        return `
        <div class="inv-summary-item${idx > 0 ? ' inv-summary-item--next' : ''}">
          ${invoices.length > 1 ? `<div class="invoice-kicker">Invoice ${idx + 1} of ${invoices.length}</div>` : ''}
          <div class="invoice-no">${invNo}</div>
          ${invDate ? `<div class="invoice-date">Invoice date: ${invDate}</div>` : ''}
          ${statusLabel ? `<div class="inv-status">Status: ${statusLabel}</div>` : ''}
        </div>`;
      })
      .join('');
    appliedInvoiceHtml = `
    <div class="panel panel--invoice">
      <div class="panel-title">Applied to invoice${invoices.length > 1 ? 's' : ''}</div>
      ${invItems}
    </div>`;
  }

  let invoiceBlocks = '';
  invoices.forEach((inv) => {
    const invNo = escapeHtmlForPdf(inv.invoiceNo || '');
    const records = recordsForInvoice(inv, allocationRows);
    const summary = summarizeRecords(records);

    invoiceBlocks += `
    <section class="invoice-block">
      ${
        invoices.length > 1
          ? `<div class="invoice-lines-kicker">Lines for ${invNo}</div>`
          : ''
      }
      <table class="lines-table">
        <thead>
          <tr>
            <th class="col-desc">Description</th>
            <th class="col-amt">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${buildLineRows(records)}
        </tbody>
      </table>
      ${buildTotalsBlock(summary)}
    </section>`;
  });

  const invoiceSection = invoiceBlocks || '';

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"/><title>Receipt ${ref}</title>
<style>
  @page { margin: 16mm 14mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #111;
    font-family: Georgia, 'Times New Roman', Times, serif;
    font-size: 12.5px;
    line-height: 1.45;
    -webkit-font-smoothing: antialiased;
  }
  .receipt {
    max-width: 640px;
    margin: 0 auto;
    padding: 28px 32px 36px;
    background: #fff;
  }
  .letterhead {
    text-align: center;
    border-bottom: 2px solid #111;
    padding-bottom: 14px;
    margin-bottom: 18px;
  }
  .company {
    font-size: 20px;
    font-weight: 700;
    letter-spacing: 0.02em;
    margin: 0 0 4px;
  }
  .doc-title {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
  }
  .amount-due {
    text-align: center;
    margin: 18px 0 20px;
  }
  .amount-due .label {
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #444;
  }
  .amount-due .value {
    font-size: 28px;
    font-weight: 700;
    margin-top: 4px;
    font-variant-numeric: tabular-nums;
  }
  .top-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px 24px;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #ccc;
  }
  .panel-title {
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #666;
    margin-bottom: 8px;
    font-weight: 700;
  }
  .meta {
    width: 100%;
    border-collapse: collapse;
  }
  .meta td {
    padding: 3px 0;
    vertical-align: top;
  }
  .meta .k {
    width: 42%;
    color: #555;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding-right: 8px;
  }
  .meta .v {
    font-weight: 600;
    font-family: 'Segoe UI', system-ui, sans-serif;
    font-size: 12.5px;
  }
  .invoice-kicker {
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #666;
    margin-bottom: 2px;
  }
  .invoice-no {
    font-size: 14px;
    font-weight: 700;
    font-family: 'Segoe UI', system-ui, sans-serif;
  }
  .invoice-date,
  .inv-status {
    font-size: 12px;
    color: #444;
    margin-top: 2px;
    font-family: 'Segoe UI', system-ui, sans-serif;
  }
  .inv-summary-item--next {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px dotted #ccc;
  }
  .invoice-block {
    margin-bottom: 18px;
  }
  .invoice-block + .invoice-block {
    border-top: 1px solid #ccc;
    padding-top: 16px;
  }
  .invoice-lines-kicker {
    font-size: 11px;
    font-weight: 700;
    margin-bottom: 6px;
    font-family: 'Segoe UI', system-ui, sans-serif;
  }
  .lines-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 4px;
    font-family: 'Segoe UI', system-ui, sans-serif;
  }
  .lines-table th {
    text-align: left;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #555;
    border-bottom: 1px solid #111;
    padding: 6px 0;
  }
  .lines-table th.col-amt,
  .lines-table td.col-amt {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
  .lines-table td {
    padding: 9px 0;
    border-bottom: 1px dotted #ccc;
    vertical-align: top;
  }
  .desc-title {
    font-weight: 600;
    color: #111;
  }
  .desc-detail {
    color: #555;
    font-size: 12px;
    margin-top: 2px;
  }
  .col-desc { width: 72%; padding-right: 12px; }
  .col-amt { width: 28%; }
  .totals-table {
    width: 100%;
    max-width: 280px;
    margin: 14px 0 0 auto;
    border-collapse: collapse;
    font-family: 'Segoe UI', system-ui, sans-serif;
  }
  .totals-table td {
    padding: 4px 0;
    font-size: 12.5px;
  }
  .totals-table td:last-child {
    text-align: right;
    font-variant-numeric: tabular-nums;
    padding-left: 16px;
    white-space: nowrap;
  }
  .tot-sub td {
    border-top: 1px solid #bbb;
    padding-top: 8px;
  }
  .tot-final td {
    border-top: 2px solid #111;
    padding-top: 8px;
    font-size: 14px;
    font-weight: 700;
  }
  .footer {
    margin-top: 28px;
    padding-top: 14px;
    border-top: 1px solid #111;
    text-align: center;
  }
  .thanks {
    font-size: 13px;
    margin-bottom: 6px;
  }
  .foot-meta {
    font-size: 11px;
    color: #666;
    font-family: 'Segoe UI', system-ui, sans-serif;
  }
  .muted { color: #777; }
  @media (max-width: 520px) {
    .top-grid { grid-template-columns: 1fr; }
  }
</style></head><body>
<div class="receipt">
  <header class="letterhead">
    <p class="company">${co}</p>
    <p class="doc-title">Payment Receipt</p>
  </header>

  <div class="amount-due">
    <div class="label">Amount received</div>
    <div class="value">$${total}</div>
  </div>

  <div class="top-grid">
    ${receiptDetailsHtml}
    ${appliedInvoiceHtml}
  </div>

  ${invoiceSection}

  <footer class="footer">
    <div class="thanks">Thank you for your payment.</div>
    <div class="foot-meta">Generated ${generated}<br/>Questions? Contact ${co}</div>
  </footer>
</div>
</body></html>`;
}

export function base64ToPdfBlob(base64) {
  const clean = String(base64).replace(/\s/g, '');
  const binary = atob(clean);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: 'application/pdf' });
}

export function triggerPdfDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'receipt.pdf';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
